import React, { useState, useEffect } from 'react';
import {
  Dialog,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Box,
  ListItemText,
  Typography,
  Chip,
  IconButton,
  Switch,
  useMediaQuery,
  useTheme,
  Grid,
  alpha,
} from '@mui/material';
import { format } from 'date-fns';
import { reservationApi } from '../reservation_api';
import { Court } from '../api_types';
import CloseIcon from '@mui/icons-material/Close';
import ConflictStatus from '../shared/components/ConflictStatus';

interface EventModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: EventData) => Promise<void>;
  onDelete?: (id: string) => Promise<void>;
  courts: Court[];
  event: EventData | null;
  mode: 'create' | 'edit';
  timeOptions: string[];
  onDateChange: (date: string) => void;
  facilityId: string;
  loading?: boolean;
  businessId: string;
}

export interface RecurrenceRule {
  frequency: 'daily' | 'weekly' | 'monthly' | 'custom';
  interval?: number; // For custom frequency (e.g., every 2 weeks)
  daysOfWeek?: number[]; // For weekly (0-6, Sunday-Saturday)
  dayOfMonth?: number; // For monthly
  endDate?: string; // Optional end date for the series
  occurrences?: number; // Optional number of occurrences
}

export interface EventData {
  id?: string;
  title: string;
  description: string;
  date: string;
  startTime: string;
  endTime: string;
  courtIds: string[];
  capacity?: number;
  registrationRequired: boolean;
  registrationDeadline?: string;
  price?: number;
  status: 'scheduled' | 'cancelled' | 'completed';
  isRecurring?: boolean;
  recurrenceRule?: RecurrenceRule;
  parentEventId?: string; // For recurring event instances
  participants?: {
    userId: string;
    signUpDate: string;
    name?: string;
    participantId?: string;
  }[];
  isBlocked?: boolean; // Added for blocked events
}

const EventModal: React.FC<EventModalProps> = ({
  open,
  onClose,
  onSave,
  onDelete,
  courts,
  event,
  mode,
  timeOptions,
  onDateChange,
  facilityId,
  loading = false,
  businessId
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const [formData, setFormData] = useState<EventData>(
    event || {
      title: '',
      description: '',
      date: format(new Date(), 'yyyy-MM-dd'),
      startTime: '',
      endTime: '',
      courtIds: [],
      registrationRequired: false,
      status: 'scheduled',
      participants: [],
      isBlocked: false
    }
  );
  const [timeError, setTimeError] = useState<string | null>(null);
  const [hasConflict, setHasConflict] = useState(false);
  const [isCheckingConflicts, setIsCheckingConflicts] = useState(false);

  useEffect(() => {
    if (event) {
      setFormData({
        ...event,
        id: event.id,
        date: event.date,
        startTime: event.startTime,
        endTime: event.endTime,
        courtIds: event.courtIds || [],
        registrationRequired: event.registrationRequired ?? false
      });
      if (event.date !== formData.date) {
        onDateChange(event.date);
      }
    } else {
      const today = format(new Date(), 'yyyy-MM-dd');
      setFormData({
        title: '',
        description: '',
        date: today,
        startTime: '',
        endTime: '',
        courtIds: [],
        registrationRequired: false,
        status: 'scheduled',
        participants: [],
        isBlocked: false
      });
      if (today !== formData.date) {
        onDateChange(today);
      }
    }
  }, [event]);

  // Conflict check effect
  useEffect(() => {
    async function checkConflicts() {
      setTimeError(null);
      setHasConflict(false);
      if (!formData.startTime || !formData.endTime || formData.courtIds.length === 0) return;
      
      setIsCheckingConflicts(true);
      try {
        console.log('[EventModal] Checking conflicts with:', {
          facilityId,
          date: formData.date,
          startTime: formData.startTime,
          endTime: formData.endTime,
          courtIds: formData.courtIds,
          eventId: formData.id,
          mode
        });
        
        // Batch check all courts in parallel instead of sequentially
        const conflictChecks = formData.courtIds.map(courtId => {
          const params = {
            courtId,
            date: formData.date,
            startTime: formData.startTime,
            endTime: formData.endTime,
            excludeEventId: formData.id,
            checkRecurringEvents: true,
            checkOneTimeEvents: true,
            checkReservations: true,
            checkClasses: true
          };
          console.log('[EventModal] Conflict check params:', params);
          return reservationApi.checkTimeConflicts(businessId, facilityId, params);
        });
        
        const results = await Promise.all(conflictChecks);
        console.log('[EventModal] Conflict check results:', results);
        
        const conflictResult = results.find(result => result.hasConflict);
        if (conflictResult) {
          setTimeError(conflictResult.conflictMessage || 'There is a scheduling conflict.');
          setHasConflict(true);
        }
      } finally {
        setIsCheckingConflicts(false);
      }
    }

    // Debounce conflict checking to avoid excessive API calls
    const timeoutId = setTimeout(checkConflicts, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.date, formData.startTime, formData.endTime, formData.courtIds, facilityId, formData.id, mode]);

  const handleChange = (field: keyof EventData, value: any) => {
    if (field === 'date') {
      onDateChange(value);
    }
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTimeError(null);
    if (hasConflict) {
      console.warn('[EventModal] Tried to submit with a conflict:', timeError);
      return;
    }
    console.log('[EventModal] Submitting event:', formData);
    
    if (!timeOptions.includes(formData.startTime)) {
      setTimeError("Selected start time is outside business hours");
      return;
    }

    if (!timeOptions.includes(formData.endTime)) {
      setTimeError("Selected end time is outside business hours");
      return;
    }

    const [startHour, startMinute] = formData.startTime.split(':').map(Number);
    const [endHour, endMinute] = formData.endTime.split(':').map(Number);
    
    if (endHour < startHour || (endHour === startHour && endMinute <= startMinute)) {
      setTimeError("End time must be later than start time");
      return;
    }

    // Check for conflicts using the new API
    try {
      for (const courtId of formData.courtIds) {
        const result = await reservationApi.checkTimeConflicts(businessId, facilityId, {
          courtId,
          date: formData.date,
          startTime: formData.startTime,
          endTime: formData.endTime,
          excludeEventId: formData.id,
          checkRecurringEvents: true,
          checkOneTimeEvents: true,
          checkReservations: true,
          checkClasses: true
        });

        if (result.hasConflict) {
          setTimeError(result.conflictMessage || 'There is a scheduling conflict.');
          return;
        }
      }

      // If no conflicts, proceed
      await onSave(formData);
    } catch (error) {
      if (error instanceof Error) {
        setTimeError(error.message);
      } else {
        setTimeError('Failed to save event. Please try again.');
      }
    }
  };

  const handleDelete = async () => {
    if (!event?.id || !onDelete) return;
    
    if (!window.confirm('Are you sure you want to delete this event?')) {
      return;
    }

    await onDelete(event.id);
    onClose();
  };

  const handleRemoveParticipant = (participantId: string) => {
    setFormData(prev => ({
      ...prev,
      participants: prev.participants?.filter(p => p.participantId !== participantId)
    }));
  };

  const selectedCourtNames = formData.courtIds
    .map(courtId => courts.find(c => c.id === courtId)?.name || 'Unknown Court')
    .join(', ');

  // Form validation - check if all required fields are filled
  const isFormValid = () => {
    return Boolean(
      formData.title.trim() &&
      formData.date &&
      formData.startTime &&
      formData.endTime &&
      formData.courtIds.length > 0
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 30px 60px -12px rgba(50,50,93,0.25),0 18px 36px -18px rgba(0,0,0,0.3)',
          overflow: 'hidden',
          maxHeight: '90vh',
          m: isMobile ? 2 : 4,
        }
      }}
    >
      <Box sx={{ 
        p: { xs: 2, sm: 3, md: 4 },
        overflowY: 'auto',
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 2, md: 4 } }}>
          <Typography variant="h5" sx={{ 
            fontSize: { xs: '20px', md: '24px' },
            fontWeight: 600,
            color: '#1a1f36',
            letterSpacing: '-0.01em',
          }}>
            {mode === 'create' ? 'Create new event' : 'Edit event'}
          </Typography>
          <IconButton 
            onClick={onClose} 
            size="small"
            sx={{
              color: '#697386',
              '&:hover': {
                backgroundColor: '#f6f8fa',
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              placeholder="Event title *"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              required
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: '#f6f8fa',
                  border: '1px solid #e3e8ee',
                  '&:hover': {
                    backgroundColor: '#fff',
                    borderColor: '#c1c9d2',
                  },
                  '&.Mui-focused': {
                    backgroundColor: '#fff',
                    borderColor: '#625afa',
                    boxShadow: '0 0 0 4px rgba(98,90,250,0.1)',
                  },
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
              }}
            />

            <TextField
              placeholder="Description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              multiline
              rows={3}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: '#f6f8fa',
                  border: '1px solid #e3e8ee',
                  '&:hover': {
                    backgroundColor: '#fff',
                    borderColor: '#c1c9d2',
                  },
                  '&.Mui-focused': {
                    backgroundColor: '#fff',
                    borderColor: '#625afa',
                    boxShadow: '0 0 0 4px rgba(98,90,250,0.1)',
                  },
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
              }}
            />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1f36', fontWeight: 500 }}>
                  Date *
                </Typography>
                <TextField
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleChange('date', e.target.value)}
                  required
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      backgroundColor: '#f6f8fa',
                      border: '1px solid #e3e8ee',
                      '&:hover': {
                        backgroundColor: '#fff',
                        borderColor: '#c1c9d2',
                      },
                      '&.Mui-focused': {
                        backgroundColor: '#fff',
                        borderColor: '#625afa',
                        boxShadow: '0 0 0 4px rgba(98,90,250,0.1)',
                      },
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1f36', fontWeight: 500 }}>
                  Start time *
                </Typography>
                <FormControl fullWidth error={!!timeError}>
                  <Select
                    value={formData.startTime}
                    onChange={(e) => handleChange('startTime', e.target.value)}
                    required
                    sx={{
                      borderRadius: '8px',
                      backgroundColor: '#f6f8fa',
                      border: '1px solid #e3e8ee',
                      '&:hover': {
                        backgroundColor: '#fff',
                        borderColor: '#c1c9d2',
                      },
                      '&.Mui-focused': {
                        backgroundColor: '#fff',
                        borderColor: '#625afa',
                        boxShadow: '0 0 0 4px rgba(98,90,250,0.1)',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                    }}
                  >
                    {timeOptions.map((time) => (
                      <MenuItem key={time} value={time}>
                        {format(new Date(`2000-01-01T${time}`), 'h:mm a')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1f36', fontWeight: 500 }}>
                  End time *
                </Typography>
                <FormControl fullWidth error={!!timeError}>
                  <Select
                    value={formData.endTime}
                    onChange={(e) => handleChange('endTime', e.target.value)}
                    required
                    sx={{
                      borderRadius: '8px',
                      backgroundColor: '#f6f8fa',
                      border: '1px solid #e3e8ee',
                      '&:hover': {
                        backgroundColor: '#fff',
                        borderColor: '#c1c9d2',
                      },
                      '&.Mui-focused': {
                        backgroundColor: '#fff',
                        borderColor: '#625afa',
                        boxShadow: '0 0 0 4px rgba(98,90,250,0.1)',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                    }}
                  >
                    {timeOptions.map((time) => (
                      <MenuItem key={time} value={time}>
                        {format(new Date(`2000-01-01T${time}`), 'h:mm a')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1f36', fontWeight: 500 }}>
                Courts *
              </Typography>
              <FormControl fullWidth>
                <Select
                  multiple
                  displayEmpty
                  value={formData.courtIds}
                  onChange={(e) => handleChange('courtIds', e.target.value)}
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return <Typography sx={{ color: '#697386' }}>Select courts</Typography>;
                    }
                    return selectedCourtNames;
                  }}
                  sx={{
                    borderRadius: '8px',
                    backgroundColor: '#f6f8fa',
                    border: '1px solid #e3e8ee',
                    '&:hover': {
                      backgroundColor: '#fff',
                      borderColor: '#c1c9d2',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#fff',
                      borderColor: '#625afa',
                      boxShadow: '0 0 0 4px rgba(98,90,250,0.1)',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                  }}
                >
                  {courts.map((court) => (
                    <MenuItem key={court.id} value={court.id}>
                      <Checkbox 
                        checked={formData.courtIds.includes(court.id)}
                        sx={{
                          color: '#625afa',
                          '&.Mui-checked': {
                            color: '#625afa',
                          },
                        }}
                      />
                      <ListItemText primary={court.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1f36', fontWeight: 500 }}>
                  Capacity
                </Typography>
                <TextField
                  type="number"
                  placeholder="0"
                  value={formData.capacity || ''}
                  onChange={(e) => handleChange('capacity', parseInt(e.target.value) || undefined)}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      backgroundColor: '#f6f8fa',
                      border: '1px solid #e3e8ee',
                      '&:hover': {
                        backgroundColor: '#fff',
                        borderColor: '#c1c9d2',
                      },
                      '&.Mui-focused': {
                        backgroundColor: '#fff',
                        borderColor: '#625afa',
                        boxShadow: '0 0 0 4px rgba(98,90,250,0.1)',
                      },
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1f36', fontWeight: 500 }}>
                  Price
                </Typography>
                <TextField
                  type="number"
                  placeholder="$0"
                  value={formData.price || ''}
                  onChange={(e) => handleChange('price', parseFloat(e.target.value) || undefined)}
                  fullWidth
                  InputProps={{
                    startAdornment: <Typography sx={{ color: '#697386', mr: 1 }}>$</Typography>,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      backgroundColor: '#f6f8fa',
                      border: '1px solid #e3e8ee',
                      '&:hover': {
                        backgroundColor: '#fff',
                        borderColor: '#c1c9d2',
                      },
                      '&.Mui-focused': {
                        backgroundColor: '#fff',
                        borderColor: '#625afa',
                        boxShadow: '0 0 0 4px rgba(98,90,250,0.1)',
                      },
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                  }}
                />
              </Grid>
            </Grid>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.registrationRequired ?? false}
                  onChange={(e) => handleChange('registrationRequired', e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase': {
                      '&.Mui-checked': {
                        color: '#625afa',
                        '& + .MuiSwitch-track': {
                          backgroundColor: '#625afa',
                          opacity: 0.5,
                        },
                      },
                    },
                    '& .MuiSwitch-track': {
                      borderRadius: 22 / 2,
                    },
                    '& .MuiSwitch-thumb': {
                      boxShadow: 'none',
                      width: 16,
                      height: 16,
                      margin: 2,
                    },
                  }}
                />
              }
              label={
                <Typography sx={{ color: '#1a1f36', fontWeight: 500 }}>
                  Registration required
                </Typography>
              }
            />

            {formData.registrationRequired && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1f36', fontWeight: 500 }}>
                  Registration deadline
                </Typography>
                <TextField
                  type="datetime-local"
                  value={formData.registrationDeadline || ''}
                  onChange={(e) => handleChange('registrationDeadline', e.target.value)}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      backgroundColor: '#f6f8fa',
                      border: '1px solid #e3e8ee',
                      '&:hover': {
                        backgroundColor: '#fff',
                        borderColor: '#c1c9d2',
                      },
                      '&.Mui-focused': {
                        backgroundColor: '#fff',
                        borderColor: '#625afa',
                        boxShadow: '0 0 0 4px rgba(98,90,250,0.1)',
                      },
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                  }}
                />
              </Box>
            )}

            {mode === 'edit' && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1f36', fontWeight: 500 }}>
                  Status
                </Typography>
                <FormControl fullWidth>
                  <Select
                    value={formData.status}
                    onChange={(e) => handleChange('status', e.target.value)}
                    sx={{
                      borderRadius: '8px',
                      backgroundColor: '#f6f8fa',
                      border: '1px solid #e3e8ee',
                      '&:hover': {
                        backgroundColor: '#fff',
                        borderColor: '#c1c9d2',
                      },
                      '&.Mui-focused': {
                        backgroundColor: '#fff',
                        borderColor: '#625afa',
                        boxShadow: '0 0 0 4px rgba(98,90,250,0.1)',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                    }}
                  >
                    <MenuItem value="scheduled">Scheduled</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}

            {formData.participants && formData.participants.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1f36', fontWeight: 500 }}>
                  Participants
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {formData.participants.map((participant) => (
                    <Chip
                      key={participant.participantId}
                      label={participant.name || 'Unknown User'}
                      onDelete={() => handleRemoveParticipant(participant.participantId!)}
                      sx={{
                        backgroundColor: alpha('#625afa', 0.1),
                        color: '#625afa',
                        borderRadius: '6px',
                        height: '28px',
                        fontSize: '13px',
                        '& .MuiChip-deleteIcon': {
                          fontSize: '16px',
                          color: '#625afa',
                          '&:hover': {
                            color: '#4b44c9',
                          },
                        },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            <ConflictStatus 
              hasConflict={hasConflict} 
              conflictMessage={timeError} 
              isChecking={isCheckingConflicts}
              showNoConflicts={Boolean(
                formData.startTime && 
                formData.endTime && 
                formData.courtIds.length > 0 && 
                formData.date
              )}
            />
          </Box>

          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            flexDirection: isMobile ? 'column' : 'row',
            gap: 2, 
            mt: 4,
            pt: 4,
            borderTop: '1px solid #e3e8ee',
          }}>
            {mode === 'edit' && onDelete && (
              <Button
                onClick={handleDelete}
                sx={{
                  mr: isMobile ? 0 : 'auto',
                  order: isMobile ? 3 : 1,
                  color: '#cd3d64',
                  fontSize: '14px',
                  fontWeight: 500,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: alpha('#cd3d64', 0.04),
                  },
                }}
                disabled={loading}
              >
                Delete event
              </Button>
            )}
            <Button 
              onClick={onClose}
              disabled={loading}
              sx={{
                order: isMobile ? 2 : 2,
                color: '#1a1f36',
                fontSize: '14px',
                fontWeight: 500,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#f6f8fa',
                },
              }}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained"
              disabled={loading || hasConflict || isCheckingConflicts || !isFormValid()}
              fullWidth={isMobile}
              sx={{
                order: isMobile ? 1 : 3,
                backgroundColor: '#625afa',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 500,
                textTransform: 'none',
                padding: '10px 20px',
                '&:hover': {
                  backgroundColor: '#4b44c9',
                },
              }}
            >
              {loading ? 'Saving...' : isCheckingConflicts ? 'Checking conflicts...' : mode === 'create' ? 'Create event' : 'Save changes'}
            </Button>
          </Box>
        </form>
      </Box>
    </Dialog>
  );
};

export default EventModal; 